import React, { useState, useMemo, memo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { Col, Card, Badge } from '../../../ui-kit';
import { themeType } from '../../../ui-kit/helpers';
import moment from 'moment';
import { isEqual, differenceWith } from 'lodash';
import { mergeConsecutiveIntervals } from '../../../../utils/schedules';

import IntervalCard from './IntervalCard';

const NowSectionHeader = () => (
  <ul className="list-group list-group-flush">
    <li className="list-group-item py-0 text-uppercase" style={{ background: 'var(--bs-info)' }}>
      {I18n.t('now')}
    </li>
  </ul>
);

const NextSectionHeader = ({ understaffingCount }) => (
  <ul className="list-group list-group-flush">
    <li
      className="list-group-item py-0 d-flex justify-content-between text-uppercase"
      style={{ background: themeType === 'light' ? 'var(--bs-gray-400)' : 'var(--bs-gray-800)' }}
    >
      {I18n.t('next')}{' '}
      {understaffingCount > 0 && (
        <Badge pill variant="danger" className="align-self-center">{`${understaffingCount} ${I18n.t(
          'understaffing'
        )}`}</Badge>
      )}
    </li>
  </ul>
);

const AvailabilityRequirementCard = ({
  schedule,
  skillsById,
  filters,
  maxStartTime,
  minEndTime,
  shortenTimes,
  handleContextMenu,
  showNamesWhenOrangeOrRed
}) => {
  const requirement = schedule?.availability_requirement;
  const [maxIntervals, setMaxIntervals] = useState(10);

  const intervals = useMemo(
    () =>
      (schedule?.intervals || []).map(i => ({
        ...i,
        skill_statuses: i.skill_statuses.map(ss => ({ ...ss, skill: skillsById[ss.skill_id] }))
      })),
    [schedule, skillsById]
  );

  const equalBySkillCounts = (interval1, interval2) => {
    const skillCounts1 =
      interval1?.skill_statuses?.map(ss => ({
        skill_id: ss.skill_id,
        assigned_count: ss.assigned_count
      })) || [];
    const skillCounts2 =
      interval2?.skill_statuses?.map(ss => ({
        skill_id: ss.skill_id,
        assigned_count: ss.assigned_count
      })) || [];

    return differenceWith(skillCounts1, skillCounts2, isEqual).length === 0;
  };

  const currentIntervals = useMemo(() => {
    const filtered = intervals.filter(i => moment().isBetween(i.start_time, i.end_time, undefined, '[)'));
    return mergeConsecutiveIntervals(filtered, equalBySkillCounts);
  }, [intervals]);

  const nextIntervals = useMemo(() => {
    const filtered = intervals.filter(
      i =>
        filters[i.warning_level].show &&
        ((moment().isBetween(minEndTime, maxStartTime) && moment().isBefore(i.start_time)) ||
          (!moment().isBetween(minEndTime, maxStartTime) &&
            minEndTime.isBefore(i.end_time) &&
            maxStartTime.isAfter(i.start_time)))
    );

    return mergeConsecutiveIntervals(filtered, equalBySkillCounts);
  }, [intervals]);

  const understaffings = useMemo(
    () => nextIntervals.filter(interval => interval.warning_level == 'below_required'),
    [nextIntervals]
  );

  const showMoreIntervals = () => {
    setMaxIntervals(maxIntervals + 20);
  };

  const intervalCount = isMobileOnly ? Math.min(maxIntervals, nextIntervals.length) : nextIntervals.length;

  if (!schedule) return null;
  const isNow = moment().isBetween(minEndTime, maxStartTime);

  return (
    <Col>
      <Card>
        <Card.Header
          className="px-3 py-1"
          style={{
            background: 'var(--bs-gray-800)',
            color: 'var(--bs-gray-100)'
          }}
        >
          <Card.Title>
            <h5>{requirement.name}</h5>
          </Card.Title>
        </Card.Header>
        {isNow && (
          <>
            <NowSectionHeader />
            <Card.Body className="p-2">
              {currentIntervals.map(interval => (
                <IntervalCard
                  key={interval.start_time}
                  className={currentIntervals.length > 1 && 'mb-2'}
                  requirement={requirement}
                  interval={interval}
                  shortenTimes={shortenTimes}
                  expandByDefault={true}
                  handleContextMenu={handleContextMenu}
                  showNamesWhenOrangeOrRed={showNamesWhenOrangeOrRed}
                />
              ))}
            </Card.Body>
            <NextSectionHeader understaffingCount={understaffings.length} />
          </>
        )}
        <Card.Body className="p-2">
          {nextIntervals.slice(0, intervalCount).map(interval => (
            <IntervalCard
              key={interval.start_time}
              className="mb-2"
              requirement={requirement}
              interval={interval}
              expandByDefault={filters[interval.warning_level].expand}
              shortenTimes={shortenTimes}
              handleContextMenu={handleContextMenu}
              showNamesWhenOrangeOrRed={showNamesWhenOrangeOrRed}
            />
          ))}
          {isMobileOnly && nextIntervals.length - maxIntervals > 0 && (
            <div className="d-flex justify-content-center">
              <small className="text-center" onClick={showMoreIntervals}>
                {I18n.t('show_more')} {`(${Math.max(nextIntervals.length - maxIntervals, 0)})`}
              </small>
            </div>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

export default memo(AvailabilityRequirementCard);
